﻿// Dependencies
import '@babel/polyfill';
import 'whatwg-fetch';
import 'core-js/es/object';
import 'core-js/es/string';
import 'core-js/es/array';
import 'mutationobserver-shim';
import 'custom-event-polyfill';
import 'element-remove';
import 'element-closest-polyfill';

import domready from 'domready';
import iframeResize from 'iframe-resizer/js/iframeResizer';
import jump from 'jump.js';
import throttle from 'lodash.throttle';

// Project
import { default as config } from './Project/configuration';

// Foundation
import {
  Collapsible,
  CollapsibleOpen,
  sitecore,
  MediaQuery,
  LazyLoad,
  utils,
  Cookiebot,
  HideElement,
} from './Foundation';

// Features
import {
  MainNavigation,
  BackgroundMedia,
  SwiperContainer,
  Video,
  VideoLightbox,
  Select,
  Datepicker,
  Login,
  ServiceMenu,
  Slider,
  Tooltip,
  Accessory,
  NumberInput,
  Cart,
  CartLine,
  EarlyPaymentDiscount,
  EqualHeight,
  ProductFilter,
  ProductSearch,
  ProductTree,
  OrderLines,
  TimeoutBox,
  Dropable,
  LeadManagement,
  DataTable,
  PageControls,
  VideoMatrix,
  FullWidthCarousel,
  StickyMenu,
  HeroScroll,
  FeatureScroll,
  AudioPlayer,
  BTEConfigurator,
  PAConfigurator,
  Modalify,
  PageLoading,
  Progress,
  CheckboxFilter,
  Warranty,
  LoyaltyDashboard,
  RewardConfigurator,
  CourseView,
  MyAchievements,
  LMSRibbon,
  NumbersOnly,
  CurrencyFilter,
  DataTableSpot,
  ProductDropdown,
  EnableButtonOnload,
  ReturnForCredit,
  ProductSpot,
  ProductPagination,
  ColorDropdown,
  AccessoriesCarousel,
  ExtraSalesModal,
  ProductFeatureSpot,
  MegaMenuNavigation,
  ITEConfiguratorHCP,
  Multiselect,
  FieldDepencencyController,
  PIMImage,
  MarketingCloudForms,
  BarcodeScanner,
  Card,
  SideNav,
  SavingsAndEarnings,
  Calculator,
  HideEmptySections,
  CardNextLevel,
  ColorSpot,
  CompatibleProductsModal,
  BentoExpandable,
} from './Feature';

class App {
  constructor() {
    // Implemented here to avoid old design flashing before the full page load
    this.applyNewCartDesign();
    // Initiate page loading
    this.initializeComponent('.page-loading', PageLoading);
    // Remove invalid URL search params
    this.removeInvalidParams();
    // Check invalid links
    this.checkInvalidLinks();
    // Check if dom is ready
    if (document.readyState === 'complete') {
      this.load();
    } else {
      domready(() => this.load());
    }
  }

  /**
   * Load
   * Execute a sequence of functions once document is loaded
   */
  load() {
    // Web component
    this.initializeWebComponent();

    // Initialize components
    this.initializeComponents();

    // Event Listeners
    this.addGeneralEventListeners();
  }

  /**
   * Initializes all the components currently on the target element
   */
  initializeComponents() {
    this.initializeComponent('[data-hide-element], [data-hide-children]', HideElement);

    // # Spots
    this.initializeComponent('[data-extra-sales]', ExtraSalesModal);
    this.initializeComponent('.main-navigation', MainNavigation);
    this.initializeComponent('.service-menu', ServiceMenu);
    this.initializeComponent('.background-media', BackgroundMedia);
    this.initializeComponent('.accordion, .details-expander', Collapsible);
    this.initializeComponent('.accordion-open', CollapsibleOpen);
    this.initializeComponent('.video', Video);
    this.initializeComponent('[data-tooltip]', Tooltip);
    this.initializeComponentUniquely('[data-slider]', Slider, { identifier: 'slider-id-' });
    this.initializeComponentUniquely('[data-swiper]', SwiperContainer, {
      identifier: 'swiper-id-',
    });
    this.initializeComponentUniquely('[data-product-spot]', ProductSpot, {
      identifier: 'product-spot-id-',
    });
    this.initializeComponent('.js-equal-height', EqualHeight);
    this.initializeComponent('[data-module="CourseView"]', CourseView);
    this.initializeComponent('[data-module="MyAchievements"]', MyAchievements);
    this.initializeComponent('[data-module="LMSRibbon"]', LMSRibbon);
    this.initializeComponent('[data-module="mega-menu-navigation"]', MegaMenuNavigation);
    this.initializeComponent('.product-feature', ProductFeatureSpot);

    // UI Components
    this.initializeComponent('[data-login]', Login);
    this.initializeComponent('.cart-line', CartLine);
    this.initializeComponent('.cart', Cart);
    this.initializeComponent('.paymentMethods', EarlyPaymentDiscount);
    this.initializeComponent('.product-filter', ProductFilter);
    this.initializeComponent('.product-search', ProductSearch);
    this.initializeComponent('.product-tree', ProductTree);
    this.initializeComponent('[data-pagination]', ProductPagination);
    this.initializeComponent('.timeout-box', TimeoutBox);
    this.initializeComponent('[data-dropable]', Dropable);
    this.initializeComponent('.lead-management', LeadManagement);
    this.initializeComponentUniquely('[data-module="DataTable"]', DataTable, {
      identifier: 'data-table-id-',
    });
    this.initializeComponentUniquely('[data-module="DatatableSpot"]', DataTableSpot, {
      identifier: 'data-generic-table-id-',
    });
    this.initializeComponentUniquely('input[data-validation-barcode]', BarcodeScanner, {
      identifier: 'data-barcode-scanner-id-',
    });
    this.initializeComponent('.page-controls:not(.is-hidden)', PageControls);
    this.initializeComponent('.checkbox-filter', CheckboxFilter);
    this.initializeComponent('.configurator__multiselect', Multiselect);
    this.initializeComponent('[data-warranty]', Warranty);
    this.initializeComponent('[data-motion-count]', Card);
    this.initializeComponent('[data-side-nav]', SideNav);
    this.initializeComponent('[data-savings-and-earnings]', SavingsAndEarnings);
    this.initializeComponent('[data-calculator]', Calculator);
    this.initializeComponent('[data-card-next-level]', CardNextLevel);

    this.initializeComponentUniquely('[data-modalify]', Modalify, { identifier: 'modalify-' });

    this.initializeComponent('.video-matrix', VideoMatrix);
    this.initializeComponent('.product-menu', StickyMenu);
    this.initializeComponent('.product-carousel', FullWidthCarousel);
    this.initializeComponent('.hero-scroll', HeroScroll);
    this.initializeComponent('.feature-scroll', FeatureScroll);
    this.initializeComponent('.audio-player', AudioPlayer);
    this.initializeComponent('.has-progress-callback', Progress);

    this.initializeComponent('.configurator__accessories', Accessory);
    this.initializeComponent('[data-module="LoyaltyDashboard"]', LoyaltyDashboard);
    this.initializeComponent('.DeManager', MarketingCloudForms);
    this.initializeComponent('.loyalty', HideEmptySections);

    // Form elements
    this.initializeComponent('[data-select]', Select);
    this.initializeComponent('[data-datepicker]', Datepicker);
    this.initializeComponent('.number-input', NumberInput);
    this.initializeComponent('[data-filter="numbers-only"]', NumbersOnly);
    this.initializeComponent('[data-filter="currency"]', CurrencyFilter);

    // Configurators
    this.initializeComponent('#cssConfigurationBTE', BTEConfigurator);
    this.initializeComponent('#cssPAConfiguration', PAConfigurator);
    this.initializeComponent('#cssConfigurationReward', RewardConfigurator);
    this.initializeComponent('.return-for-credit', ReturnForCredit);

    // Page controllers
    this.initializeComponent('[data-orderlines]', OrderLines);
    this.initializeComponent('[data-video-lightbox]', VideoLightbox);

    this.initializeComponent('.pim-image-container', PIMImage);

    // Ensure JS is loaded before remove disabled from buttons
    // Should be placed last
    this.initializeComponent('.enable_button_onload', EnableButtonOnload);

    this.initializeComponent('.configurator', FieldDepencencyController);
    this.initializeComponent('[data-compatible-products-modal="trigger"]', CompatibleProductsModal);
  }

  /**
   * Initializes a component class on an array of dom nodes and
   * @param {target} target the target element to query for elements
   * @param {string} selector selector to specify which dom elements to initialize the component type on
   * @param {Function} Component the component class to initialize
   * @param {object} options an options object that will be passed to the component class constructor
   */
  initializeComponent(selector, Component, options) {
    /**
     * node[Component.name] has been removed due to tinyify plugin rename all Component.name to e.
     * This will cause issue if one element uses multiple components.
     * node[Component.name] = save class name to prevent multiple calling
     */
    Array.from(document.querySelectorAll(selector)).forEach((node) => new Component(node, options));
  }

  /**
   * Makes a component class unique before initialization
   * @param {string} selector selector to specify which dom elements to initialize the component type on
   * @param {Function} Component the component class to initialize
   * @param {object} options an options object that will be passed to the component class constructor
   */
  initializeComponentUniquely(selector, Component, options) {
    Array.from(document.querySelectorAll(selector)).forEach((node, index) => {
      if (!utils.hasId(node)) {
        node.setAttribute('id', options.identifier + index);
      }
      if (!node[Component.name]) {
        node[Component.name] = new Component(node, options);
      }
    });
  }

  /**
   * Collection of event listeners for bootstrapping our application
   * Lazyload, MediaQueryManager, UpdatePanelManager, ScrollToManager
   */
  addGeneralEventListeners() {
    // Lazyload
    /* eslint-disable-next-line */
    const oLazyload = new LazyLoad();

    // Cookiebot
    /* eslint-disable-next-line */
    const oCookiebot = new Cookiebot();

    // iFrame
    iframeResize(
      config.iframeResizerSettings,
      'iframe[src*="go.gn.com"], iframe[src*="go.gnhearing.com"], iframe[src*="go.pardot.com"], .pardot-iframe',
    );

    // MediaQuery
    MediaQuery.init();
    window.addEventListener(
      'change.mq',
      throttle(
        () => {
          this.initializeComponent('[data-video-lightbox]', VideoLightbox);
          this.initializeComponent('.product-feature-spot', ProductFeatureSpot);
        },
        30,
        { leading: true },
      ),
    );

    // Updatepanel handling
    if (typeof window.Sys !== 'undefined') {
      let manager = window.Sys.WebForms.PageRequestManager.getInstance();
      if (typeof manager !== 'undefined' && manager !== null) {
        manager.add_endRequest(() => {
          this.initializeComponent('[data-tooltip]', Tooltip);
          this.initializeComponent('[data-select]', Select);
          this.initializeComponent('[data-datepicker]', Datepicker);
          this.initializeComponent('.configurator__accessories', Accessory);
          this.initializeComponent('.number-input', NumberInput);
          this.initializeComponent('.cart-line', CartLine);
          this.initializeComponentUniquely('[data-product-spot]', ProductSpot, {
            identifier: 'product-spot-id-',
          });
        });
      }
    }

    // Link
    if (!sitecore.isInPageEditor) {
      const oLinks = Array.from(document.querySelectorAll('a[href^="#"]')),
        oHeader = document.querySelector('#header'),
        strHash = window.location.hash;

      // Calculate offset
      let offset = 0;
      if (oHeader != null) {
        offset -= oHeader.getBoundingClientRect().height;
      }

      // Media-query based
      if (MediaQuery.above('lg')) {
        const oSecondaryMenu = oHeader.querySelector(
          '.main-navigation__item.secondary-level-is-expanded',
        );
        if (oSecondaryMenu != null) {
          offset -= oSecondaryMenu.getBoundingClientRect().height;
        }
      }

      oLinks.forEach((el) => {
        /**
         * Recheck invalid links when DOM is ready
         */
        this.checkInvalidLink(el);

        /**
         * Event listener on links
         */
        el.addEventListener('click', () => {
          const oTarget = el.getAttribute('href');
          if (oTarget.length > 1) {
            const oAnchorTarget = document.getElementById(oTarget.replace('#', ''));
            if (oAnchorTarget) {
              jump(oAnchorTarget, {
                offset: offset,
                duration: 300,
                callback: () => {},
              });
            }
          }
        });
      });

      // Move view port to selected ID
      if (strHash) {
        const oTarget = document.querySelector(strHash);
        if (oTarget !== null) {
          setTimeout(() => {
            // Set a small timeout to ensure load-mechanisms has been fully completed
            const offset = -oHeader.getBoundingClientRect().height;
            jump(oTarget, {
              offset: offset,
              duration: 300,
              callback: () => {
                console.log('Done scrolling');
              },
            });
          }, 100);
        }
      }
    }
  }

  /**
   * Check if internal link is valid
   */
  checkInvalidParams(string) {
    const globalRegex = new RegExp('^#\\d', 'gm');
    return globalRegex.test(string);
  }

  /**
   * Remove invalid link from URL search params
   */
  removeInvalidParams() {
    const urlHash = window.location.hash;
    const isInvalidHash = this.checkInvalidParams(urlHash);
    if (isInvalidHash) {
      window.location.hash = '';
    }
  }

  /**
   * Internal link cannot start with number
   * Action: Change invalid links and IDs
   */
  checkInvalidLinks() {
    const oLinks = Array.from(document.querySelectorAll('a[href^="#"]'));
    for (const link of oLinks) {
      this.checkInvalidLink(link);
    }
  }

  checkInvalidLink(el) {
    const prefixID = 'content_id-';
    const internalLink = el.getAttribute('href');
    const isInvalidLink = this.checkInvalidParams(internalLink);
    if (isInvalidLink) {
      const newLink = internalLink.replace('#', prefixID);
      const invalidID = document.getElementById(internalLink.replace('#', ''));
      if (invalidID) {
        // Change ID
        invalidID.id = newLink;
      }
      // Change link
      el.setAttribute('href', `#${newLink}`);
    }
  }

  initializeWebComponent() {
    window.customElements.define('product-dropdown', ProductDropdown);
    window.customElements.define('accessories-carousel', AccessoriesCarousel);
    window.customElements.define('color-dropdown', ColorDropdown);
    window.customElements.define('ite-configurator-hcp', ITEConfiguratorHCP);
    window.customElements.define('bento-expandable', BentoExpandable);
    window.customElements.define('color-spot', ColorSpot);
  }

  applyNewCartDesign() {
    const container = document.querySelector('.cart');

    if (container) {
      container.style.display = 'block';
    }

    if (document.querySelector('.cart-line')) {
      const cartLine = new CartLine(document.querySelector('.cart-line'));

      if (document.getElementById('cssCart') != null) {
        document.getElementById('main-content')?.classList.add('new-cart-layout-container');
      }
      cartLine.removeOrderTypeRadioButton();
      cartLine.getConfiguratorSideText();
      cartLine.appendProductQuantity();
      cartLine.addMarginBusinessWrapperDescription();
      cartLine.adjustProductColorRow();
      cartLine.adjustEarmoldTitleStyling();
      cartLine.adjustAccessoriesTitleStyling();
      cartLine.addEarmoldSideToTitle();

      if (document.querySelector('.favorites-container')) {
        cartLine.updateDeleteButtonAlignment();
      }
    }
  }
}

export default new App();
